import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Monitor, Tablet, Smartphone, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PreviewFrame from "./PreviewFrame";
import AiPreviewBanner from "@/components/ai/AiPreviewBanner";
import { useEditor, pagePath, findSection } from "./useEditorState";

const SIZES = { desktop: 1280, tablet: 820, mobile: 390 };
const ICONS = { desktop: Monitor, tablet: Tablet, mobile: Smartphone };
const LABELS = { desktop: "Desktop", tablet: "Tablet", mobile: "Smartphone" };

export function DeviceToggle({ device, onChange, dark = false }) {
  return (
    <div className={`inline-flex rounded-lg p-0.5 ${dark ? "bg-navy-800" : "bg-slate-100"}`} data-testid="device-toggle">
      {Object.keys(SIZES).map((d) => {
        const Icon = ICONS[d];
        const active = device === d;
        return (
          <button key={d} onClick={() => onChange(d)} title={LABELS[d]} aria-label={LABELS[d]} data-testid={`device-${d}-btn`}
            className={`px-2.5 py-1.5 min-h-10 sm:min-h-0 rounded-md transition-colors ${active ? (dark ? "bg-navy-600 text-white" : "bg-white text-slate-900 shadow-sm") : (dark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")}`}>
            <Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}

export function useBoxSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

export function ScaledDevice({ device, children }) {
  const [box, { w, h }] = useBoxSize();
  const target = SIZES[device];
  const scale = w && target > w ? w / target : 1;
  return (
    <div ref={box} className="flex-1 min-h-0 w-full flex justify-center overflow-hidden">
      {w > 0 && (
        <div style={{ width: target * scale, height: h }} className="relative">
          <div style={{ width: target, height: h / scale, transform: `scale(${scale})`, transformOrigin: "top left" }}
            className={`absolute top-0 left-0 bg-white ${device !== "desktop" ? "shadow-[0_10px_40px_rgba(15,23,42,0.18)] rounded-[14px] overflow-hidden" : "shadow-sm"}`}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

// Double-click text editing in the preview: the runtime maps the text to its prop path; the change goes through the normal update (autosave, undo/redo).
function applyInlineEdit(ed, m) {
  if (m.type === "inlineEditUnavailable") { toast.message("Questo testo non si modifica qui", { description: "Non è un contenuto della sezione: usa il pannello Proprietà, il Codice o la chat AI." }); return; }
  const s = findSection(ed.content, m.id)?.section;
  if (!s) return;
  const props = structuredClone(s.props || {});
  let o = props;
  m.path.slice(0, -1).forEach((k) => { o = o?.[k]; });
  const last = m.path[m.path.length - 1];
  if (!o || typeof o[last] !== "string") return;
  o[last] = m.value;
  ed.updateSection(m.id, { props });
  ed.setSelection({ kind: "section", id: m.id });
}

export default function Canvas({ onCapture }) {
  const ed = useEditor();
  const { content, assets, ui, setUi, frameRef, selection, setSelection, setPageId, setDiagnostics, aiPreview } = ed;
  const shown = aiPreview?.content || content;
  const brand = ed.project.company_name;
  const frameContent = useMemo(() => ({ ...shown, brand_name: brand }), [shown, brand]);  // brand of the automatic menu
  const page = content.pages.find((p) => p.id === ed.pageId);
  const path = page ? pagePath(content, page) : "/";
  return (
    <div className={ui.fullscreen ? "fixed inset-0 z-50 bg-navy-950 flex flex-col" : "flex-1 min-w-0 flex flex-col canvas-dots"} data-testid="editor-canvas">
      {ui.fullscreen && (
        <div className="h-12 flex items-center justify-between px-4 border-b border-navy-800">
          <span className="text-xs text-slate-400">Anteprima a schermo intero · {page?.name}</span>
          <div className="flex items-center gap-3">
            <DeviceToggle dark device={ui.device} onChange={(d) => setUi({ device: d })} />
            <Button size="sm" variant="secondary" className="h-10 sm:h-9" onClick={() => setUi({ fullscreen: false })} data-testid="exit-fullscreen-btn"><Minimize2 size={14} className="mr-1.5" />Esci</Button>
          </div>
        </div>
      )}
      {!ui.fullscreen && <AiPreviewBanner />}
      <div className={`flex-1 min-h-0 flex flex-col ${ui.fullscreen ? "p-3" : "p-2 md:p-4"}`}>
        <ScaledDevice device={ui.device}>
          <PreviewFrame ref={frameRef} content={frameContent} assets={assets} path={path}
            selectMode={ui.selectMode && !ui.fullscreen && !ui.textPick} textPick={!!ui.textPick && !ui.fullscreen} selectedId={selection?.kind === "section" || selection?.kind === "component" ? selection.id : null}
            onRoute={(m) => { if (m.pageId) setPageId(m.pageId); }}
            onSelect={(m) => { setSelection({ kind: m.kind === "component" ? "component" : "section", id: m.id }); setUi({ rightTab: "props", rightOpen: true }); }}
            onPickText={(m) => {
              setUi({ textPick: false, rightTab: "ai", aiMode: "regen", rightOpen: true });
              if (m.id) setSelection({ kind: m.kind === "component" ? "component" : "section", id: m.id });
              ed.setPicked(m);
            }}
            onDiagnostics={setDiagnostics} onCapture={onCapture} onInlineEdit={(m) => applyInlineEdit(ed, m)} />
        </ScaledDevice>
      </div>
    </div>
  );
}
